import { useProjectStore, useUserStore } from '@/store';
import type { FontInfo, ProjectConfigData, ProjectData } from '@/types';
import {
  emitter,
  hideLoading,
  resolveProjectPaths,
  showLoading,
  syncPageLayoutDimensions
} from '@/utils';
import { until } from '@vueuse/core';
import { nextTick } from 'vue';
import { fontManager } from './FontManager';
import { fetchProjectData } from '@/modules/editor/canvas/models/template/kieStoryParser';
import { updateBook } from '@/api/book';
import { ElMessage } from 'element-plus';
import { debugCheckpoint } from '@/utils/mobileDebugConsole';
import { DEFAULT_ENABLE_WATERMARK, EVENT_NAMES } from '@/constants';

class ProjectManager {
  private static _instance: ProjectManager;

  private projectConfig: ProjectConfigData | null = null;

  constructor() {}

  public static get instance(): ProjectManager {
    if (!this._instance) {
      this._instance = new ProjectManager();
    }

    return this._instance;
  }

  public async new() {}

  public getCurrentProjectId(): string {
    const id = this.projectConfig?.id;
    return id === undefined || id === null ? '' : String(id);
  }

  public getCurrentProjectUserId(): number | undefined {
    return normalizeProjectUserId(this.projectConfig?.userId);
  }

  public async clear() {
    this.reset();
  }

  public async save(saveAs: boolean = false) {
    console.log(saveAs);

    showLoading({ text: '保存中...' });

    try {
      const projectStore = useProjectStore();

      if (!projectStore.isModify) return;

      const userStore = useUserStore();
      const { data: projectData, files } = projectStore.getSaveData();

      const fontTextMap = fontManager.getProjectFonts();
      const fonts = await fontManager.processFontSubset(
        fontTextMap,
        this.projectConfig?.id ?? Number(projectData.id)
      );

      projectData.fonts = fonts;

      // 通过 emitter 回调获取第一个 page 的 canvas 截图并上传，作为分享封面
      // 参考 BOOK_PAGE_THUMB_SYNC 的缩略图提取方式

      let coverUrl: string | null = null;
      if (projectStore.isCoverModify) {
        coverUrl = await new Promise<string | null>((resolve) => {
          emitter.emit(EVENT_NAMES.BOOK_COVER_SYNC, { resolve });
        });
      }

      const config: ProjectConfigData =
        this.projectConfig || ({ id: Number(projectData.id) } as ProjectConfigData);

      //同步用户的水印配置
      await userStore.refreshMeta();
      projectData.enable_watermark =
        userStore.userMeta.enable_watermark ?? DEFAULT_ENABLE_WATERMARK;

      config.userId ??= normalizeProjectUserId(userStore.userData.userId);
      config.project = projectData;

      // console.log(projectData, files);

      await updateBook(
        config.id,
        config,
        projectData.name ?? '未命名书本',
        coverUrl ?? '',
        files.newFileList,
        files.oldFileList
      );

      projectStore.isModify = false;
      projectStore.isCoverModify = false;

      ElMessage.success('保存成功');
    } catch (error) {
      ElMessage.error('保存失败');
      console.error(error);
    } finally {
      hideLoading();
    }
  }

  public async open(projectId?: string, options?: { showLoading?: boolean }) {
    // 有 id：工作台/编辑器预览按 id 从 CDN 拉取；
    // 无 id：阅读端独立部署，发布时已把工程 JSON 拷进构建产物目录并命名 project-config.json，
    //   读与 index.html 同级的 ./project-config.json。

    const data: ProjectConfigData = await fetchProjectData(projectId);
    debugCheckpoint('project', 'fetchProjectData:done', {
      hasProject: Boolean(data.project),
      projectId: data.id
    });

    this.projectConfig = data;

    if (!data.project) return;

    // console.log('data', data);

    const projectData: ProjectData = data.project;

    projectData.id = data.id;

    resolveProjectPaths(projectData, projectData.baseUrl);

    await this.loadProjectData(projectData, options);

    // projectStore 数据需要在这里同步，否则会被 loadProjectData 中调用的 reset 清空
    const projectStore = useProjectStore();
    projectStore.isFree = data.isFree;
  }

  public async loadProjectData(
    projectData: ProjectData | null,
    options?: { showLoading?: boolean; skipCanvasLoad?: boolean }
  ) {
    if (!projectData) {
      throw new Error('工程数据为空，无法加载');
    }

    const shouldShowLoading = options?.showLoading !== false;

    if (shouldShowLoading) {
      showLoading({ text: '加载中...' });
    }

    try {
      //同步page元素的parentWidth和parentHeight
      syncPageLayoutDimensions(projectData);

      const fonts: FontInfo[] = projectData.fonts ?? [];

      fontManager.setShowLoading(false);
      await fontManager.registerFont(...fonts);
      fontManager.setShowLoading(true);

      await this.reset();

      const projectStore = useProjectStore();

      await projectStore.load(projectData, { skipCanvasLoad: options?.skipCanvasLoad });

      // 存储字体文本快照，用于后续保存时对比判断是否需要重新子集化
      const fontTextMap = fontManager.getFontTextMapFromPages(projectData.book?.pages ?? []);
      projectStore.setFontSnapshot(fontTextMap, fonts);
    } catch (error) {
      debugCheckpoint('project', 'loadProjectData:error', error);
      console.error(error);
    } finally {
      if (shouldShowLoading) {
        hideLoading();
      }
    }
  }

  /**
   * 将配置 hydrate 到 store，供 EditorPreviewModal 使用（不触发 PROJECT_LOAD）。
   */
  public async loadPreview(config: ProjectConfigData, options?: { showLoading?: boolean }) {
    this.projectConfig = config;

    if (!config.project) {
      throw new Error('工程数据为空，无法预览');
    }

    // 2. 相对资源转绝对路径后写入 store（跳过 PROJECT_LOAD）
    resolveProjectPaths(config.project, config.project.baseUrl);
    await this.loadProjectData(config.project, {
      showLoading: options?.showLoading,
      skipCanvasLoad: true
    });
  }

  public async checkProjectLoading() {
    const projectStore = useProjectStore();

    if (!projectStore.isLoading) return;

    await until(() => projectStore.isLoading).toBe(false);
  }

  public async reset() {
    const projectStore = useProjectStore();

    projectStore.reset();

    await nextTick();
  }
}

export const projectManager = ProjectManager.instance;

function normalizeProjectUserId(value: unknown): number | undefined {
  const userId = Number(value);
  return Number.isFinite(userId) && userId > 0 ? userId : undefined;
}
